import { memo } from "react";
import { Card, Button, Image, Chip } from "@heroui/react";
import { Link, Link as RouterLink } from "@remix-run/react";
import { FiArrowUpRight, FiEye, FiShoppingCart } from "react-icons/fi";
import { formatToTwoDecimalPlaces, toKebabCase, toTitleCase } from "app/utils/fn";
import { ProductObject } from "app/api/product/product.type";
import { ProductCardProps } from "./ProductCard";
import { useAtomValue } from "jotai";
import { userAtom } from "app/atoms/user.atom";
import { UserType } from "app/api/user/user.types";

export type ProductCollectionCardProps = {
  product: ProductObject;
  gradientFrom?: string;
  gradientTo?: string;
  icon?: string;
  buttonText?: string;
  onPreview?: (product: ProductObject) => void;
};

const getCloudinaryUrl = (imageSrc: string, width: number) => {
  return imageSrc.replace(
    /https:\/\/res\.cloudinary\.com\/promodata\//,
    `https://res.cloudinary.com/promodata/f_auto,q_70,w_${width}/`,
  );
};

export const ProductCollectionCard = memo(function ProductCollectionCard({
  product,
  gradientFrom = "from-blue-100",
  gradientTo = "to-blue-200",
  icon = "🛍️",
  buttonText = "View Product",
  onPreview,
}: ProductCollectionCardProps) {
  const user = useAtomValue(userAtom);
  const productUrl = `/category/${product.category ? toKebabCase(product.category.name) : "_"}/${
    product.subCategory ? toKebabCase(product.subCategory.name) : "_"
  }/${product.slug}`;

  const getPriceDisplay = () => {
    // Get the appropriate price based on user type

    const userType = user?.userType || UserType.Non_VIP;
    const priceData = product.userTypePrice?.[userType] || product?.price;
    const minQty = product.quantity?.min ?? 0;

    if (
      priceData &&
      priceData.min > 0 &&
      (priceData.min !== priceData.max || minQty > 1 || product?.supplier?.isSpecialSupplier)
    ) {
      if (priceData.min === priceData.max) {
        return `$${formatToTwoDecimalPlaces(priceData.min)}`;
      }
      return (
        <div className="flex items-center text-sm bg-white/30 dark:bg-black/20 backdrop-blur-sm rounded-lg px-2 py-1 gap-1">
          <span className="text-gray-600 dark:text-gray-300">From</span>
          <span className="text-orange-600 dark:text-orange-400 font-semibold">
            ${formatToTwoDecimalPlaces(priceData.min)}
          </span>
          <span className="text-gray-600 dark:text-gray-300">To</span>
          <span className="text-blue-600 dark:text-blue-400 font-semibold">
            ${formatToTwoDecimalPlaces(priceData.max)}
          </span>
        </div>
      );
    }
    return "Price on request";
  };

  return (
    <Link
      to={productUrl}
      className="h-fit w-full relative overflow-hidden rounded-2xl group transition-all duration-300"
      style={{
        backgroundImage: product.overview?.heroImage
          ? `url(${getCloudinaryUrl(product.overview.heroImage, 800)})`
          : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Glassmorphism overlay */}
      <div className="absolute inset-0  shadow-sm rounded-2xl backdrop-blur-md backdrop-saturate-150 dark:bg-[rgba(17,25,40,0.80)] bg-[rgba(255,255,255,0.80)]" />

      {/* Content overlay */}
      <div className="relative z-10 flex items-center p-2">
        <div className="flex-1">
          <div className="flex items-start justify-between mb-2">
            <h3 className="text-xl font-bold text-gray-900 dark:text-white line-clamp-2">
              {toTitleCase(product.overview?.name || "")}
            </h3>
            {product.labels && product.labels.length > 0 && (
              <div className="flex flex-wrap gap-1 ml-2">
                {product.labels.slice(0, 1).map((label) => (
                  <Chip
                    key={label}
                    size="sm"
                    className={`${
                      label === "New Release"
                        ? "bg-green-700/95 backdrop-blur-sm"
                        : label === "On Sale"
                        ? "bg-blue-500/90 backdrop-blur-sm"
                        : label === "Best Buy"
                        ? "bg-orange-500/90 backdrop-blur-sm"
                        : "bg-purple-500/90 backdrop-blur-sm"
                    } text-white text-xs border border-white/20`}
                  >
                    {label}
                  </Chip>
                ))}
              </div>
            )}
          </div>

          <p className="text-gray-700 dark:text-white/90 mb-2 line-clamp-1">
            {product.product?.description || "Premium quality product"}
          </p>

          <div className="flex items-center justify-between mb-4">
            <div className="text-lg font-semibold text-gray-900 dark:text-white">
              {getPriceDisplay()}
            </div>
            {product.quantity && product.quantity.min > 0 && (
              <span className="text-sm text-gray-600 dark:text-white/80 ">
                Min: {product.quantity.min}
              </span>
            )}
          </div>
        </div>

        <div className="ml-6">
          <div className="w-24 h-24 shadow-md bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center overflow-hidden border border-white/30">
            {product.overview?.heroImage ? (
              <Image
                src={getCloudinaryUrl(product.overview.heroImage, 200)}
                alt={product.overview.name}
                className="w-full h-full object-cover rounded-xl "
                removeWrapper
              />
            ) : (
              <div className="text-3xl">{icon}</div>
            )}
          </div>
        </div>
      </div>
    </Link>
  );
});
